/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { fireGtmEvent } from '@airasia-rn/ui';

import { apiErrorMeta, errorTrackingEvent } from '../analytics';
import { getApiPlatform } from '../util/utils';
import {
  AUTH_INVALID_RESPONSE_DESCRIPTION,
  AUTH_INVALID_RESPONSE_MESSAGE,
  AUTH_LOGIN_BACKED_OFF_DESCRIPTION,
  AUTH_LOGIN_BACKED_OFF_MESSAGE,
  AUTH_REQUEST_FAILED_DESCRIPTION,
  AUTH_REQUEST_FAILED_MESSAGE_PREFIX,
  AuthBackoffError,
} from './authApiErrors';

const FLIGHTS_WEB_BASE_PATH = '/v2/flights/search';
const FLIGHTS_AUTH_TOKEN_PATH = '/api/authentication/';
const FLIGHTS_WEB_BASE_PATH_PATTERN =
  /\/v2\/flights\/search(?:\/revision-preview-[^/?#]+)?/;
// home-web is served from the root: locale-first (/en/gb) or LOB-first
// (/flight/en/cn). Other /v2 apps without their own auth route share it too.
const HOME_WEB_PATH_PATTERN =
  /^\/(?:v2(?:\/|$)|(?:[a-z-]+\/)?[a-z]{2}\/[a-z]{2}(?:\/|$))/i;

const getHomeWebBasePath = (pathname: string): string | undefined =>
  HOME_WEB_PATH_PATTERN.test(pathname) ? '' : undefined;

const getFlightsAuthTokenEndpoint = (): string => {
  if (typeof window === 'undefined' || !window.location?.pathname) {
    return `${FLIGHTS_WEB_BASE_PATH}${FLIGHTS_AUTH_TOKEN_PATH}`;
  }

  const basePathMatch = window.location.pathname.match(
    FLIGHTS_WEB_BASE_PATH_PATTERN,
  );
  const basePathEnd = basePathMatch
    ? (basePathMatch.index ?? 0) + basePathMatch[0].length
    : 0;
  const basePath = basePathMatch
    ? window.location.pathname.slice(0, basePathEnd)
    : (getHomeWebBasePath(window.location.pathname) ?? FLIGHTS_WEB_BASE_PATH);

  // Keep this same-origin. Staging/prod hosts can redirect absolute API URLs,
  // which breaks browser preflight before the request reaches the Next route.
  return `${basePath}${FLIGHTS_AUTH_TOKEN_PATH}`;
};

export type Environment = 'stg' | 'prd';

export interface AuthTokenResponse {
  accessToken: string;
  refreshToken: string | null;
  expiresAt: number;
}

// If the cached token is expiring within this window, treat it as unusable
// and fetch a fresh one instead of waiting for it to actually expire.
// Local-test skew presets (dev only): 880 (15-min) / 1180 (20-min).
// Must stay above the hook offset or scheduled refetches hit this cache.
const EXPIRY_REFRESH_BUFFER_SECONDS = 180; // 3 minutes
// const EXPIRY_REFRESH_BUFFER_SECONDS = 880; // 30 seconds cadence 15 min tokens
// const EXPIRY_REFRESH_BUFFER_SECONDS = 1180; // 30 seconds cadence 20 min tokens

// Default cooldown after a failed token request (4xx/5xx, network error,
// etc.) before trying again. For 429s we prefer the upstream's own
// Retry-After value when present. Mirrors authApiService.native.ts, which
// added this to stop a persistent failure from turning into a request storm
// where every caller immediately retries and fails again.
const DEFAULT_LOGIN_BACKOFF_MS = 30_000;

interface CacheEntry {
  token: AuthTokenResponse | null;
  // Single-flight guard: concurrent requestNewToken() calls for the same
  // geoId/channelHash share one in-flight request instead of each hitting
  // /api/authentication independently.
  inFlight: Promise<AuthTokenResponse> | null;
  backoffUntil: number;
}

// In-memory only — no sessionStorage. A full page reload always goes
// through SSR, which fetches its own fresh token server-side, so there's
// nothing worth persisting across reloads; this cache only needs to
// de-dupe concurrent client-side calls within a single page's lifetime.
const memoryCache = new Map<string, CacheEntry>();

function getCacheEntry(key: string): CacheEntry {
  let entry = memoryCache.get(key);
  if (!entry) {
    entry = { token: null, inFlight: null, backoffUntil: 0 };
    memoryCache.set(key, entry);
  }
  return entry;
}

function parseRetryAfterMs(response: Response): number {
  const headerValue = response.headers.get('Retry-After');
  if (!headerValue) {
    return DEFAULT_LOGIN_BACKOFF_MS;
  }

  // RFC 7231 allows Retry-After to be either delay-seconds or an HTTP-date.
  const seconds = Number(headerValue);
  if (Number.isFinite(seconds) && seconds > 0) {
    return seconds * 1000;
  }

  const delayMs = new Date(headerValue).getTime() - Date.now();
  return Number.isFinite(delayMs) && delayMs > 0
    ? delayMs
    : DEFAULT_LOGIN_BACKOFF_MS;
}

function isFreshEnough(token: AuthTokenResponse): boolean {
  const nowSeconds = Date.now() / 1000;
  return token.expiresAt > nowSeconds + EXPIRY_REFRESH_BUFFER_SECONDS;
}

// Test-only: module state persists across tests within the same file/process.
export function __resetAuthTokenCacheForTests(): void {
  memoryCache.clear();
}

function decodeJwtExp(jwt: string): number {
  const payload = jwt.split('.')[1];
  if (!payload) {
    // response.ok was true here - a real response came back, it just
    // doesn't match the expected shape - so this is a validation
    // failure, not an API/status error.
    fireGtmEvent({
      event: 'errors',
      errorDetails: apiErrorMeta.ERROR_TRACKING_EVENTS.ERROR_TRACKING,
      ...errorTrackingEvent({
        errorType: 'validation',
        errorDescription: AUTH_INVALID_RESPONSE_DESCRIPTION,
        errorMessage: AUTH_INVALID_RESPONSE_MESSAGE,
        screenName: getApiPlatform(),
        service: apiErrorMeta.SERVICE_NAMES.FLIGHTS_AUTHENTICATION,
      }),
    });
    throw new Error(AUTH_INVALID_RESPONSE_MESSAGE);
  }

  const base64 = payload
    .replace(/-/g, '+')
    .replace(/_/g, '/')
    .padEnd(Math.ceil(payload.length / 4) * 4, '=');
  const decoded = JSON.parse(atob(base64));

  if (typeof decoded?.exp !== 'number') {
    // response.ok was true here - a real response came back, it just
    // doesn't match the expected shape - so this is a validation
    // failure, not an API/status error.
    fireGtmEvent({
      event: 'errors',
      errorDetails: apiErrorMeta.ERROR_TRACKING_EVENTS.ERROR_TRACKING,
      ...errorTrackingEvent({
        errorType: 'validation',
        errorDescription: AUTH_INVALID_RESPONSE_DESCRIPTION,
        errorMessage: AUTH_INVALID_RESPONSE_MESSAGE,
        screenName: getApiPlatform(),
        service: apiErrorMeta.SERVICE_NAMES.FLIGHTS_AUTHENTICATION,
      }),
    });
    throw new Error(AUTH_INVALID_RESPONSE_MESSAGE);
  }

  return decoded.exp;
}

export class AuthApiService {
  constructor(
    _environment: Environment,
    private geoId: string,
    private username: string | undefined,
    private password: string | undefined,
    private channelHash: string | undefined,
  ) {}

  async requestNewToken(): Promise<AuthTokenResponse> {
    const cacheKey = `${this.geoId}:${this.channelHash ?? ''}`;
    const entry = getCacheEntry(cacheKey);

    if (entry.token && isFreshEnough(entry.token)) {
      return entry.token;
    }

    if (Date.now() < entry.backoffUntil) {
      // Client-side circuit breaker - fires before any network call, so
      // there's no HTTP status to report.
      fireGtmEvent({
        event: 'errors',
        errorDetails: apiErrorMeta.ERROR_TRACKING_EVENTS.ERROR_TRACKING,
        ...errorTrackingEvent({
          errorType: 'validation',
          errorDescription: AUTH_LOGIN_BACKED_OFF_DESCRIPTION,
          errorMessage: AUTH_LOGIN_BACKED_OFF_MESSAGE,
          screenName: getApiPlatform(),
          service: apiErrorMeta.SERVICE_NAMES.FLIGHTS_AUTHENTICATION,
        }),
      });
      throw new AuthBackoffError(AUTH_LOGIN_BACKED_OFF_MESSAGE);
    }

    if (!entry.inFlight) {
      entry.inFlight = this.fetchNewToken(entry)
        .then((result) => {
          entry.token = result;
          return result;
        })
        .finally(() => {
          entry.inFlight = null;
        });
    }

    return entry.inFlight;
  }

  private async fetchNewToken(entry: CacheEntry): Promise<AuthTokenResponse> {
    try {
      const endpoint = getFlightsAuthTokenEndpoint();
      const headers = {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      };
      const requestBody = {
        geoId: this.geoId,
        channelHash: this.channelHash,
      };

      const response = await fetch(endpoint, {
        method: 'POST',
        headers,
        body: JSON.stringify(requestBody),
      });

      let data: Record<string, unknown> = {};
      try {
        data = await response.json();
      } catch {
        data = {};
      }

      if (!response.ok) {
        entry.backoffUntil =
          Date.now() +
          (response.status === 429
            ? parseRetryAfterMs(response)
            : DEFAULT_LOGIN_BACKOFF_MS);
        const errorMessage =
          typeof data?.errorMessage === 'string'
            ? data.errorMessage
            : `${AUTH_REQUEST_FAILED_MESSAGE_PREFIX} ${response.status}`;
        // No useLogger() here - this is a plain service, not a hook/component -
        // so this calls the same GTM dispatch useLogger().logError ultimately
        // uses (see webLogger in apps/flights-web/src/app/providers.tsx)
        // directly instead.
        fireGtmEvent({
          event: 'errors',
          errorDetails: apiErrorMeta.ERROR_TRACKING_EVENTS.ERROR_TRACKING,
          ...errorTrackingEvent({
            errorType: 'api',
            errorCode: response.status,
            errorDescription: AUTH_REQUEST_FAILED_DESCRIPTION,
            errorMessage,
            screenName: getApiPlatform(),
            service: apiErrorMeta.SERVICE_NAMES.FLIGHTS_AUTHENTICATION,
          }),
        });
        throw new Error(errorMessage);
      }

      const accessToken =
        typeof data?.accessToken === 'string' ? data.accessToken : data?.jwt;
      const refreshToken = data?.refreshToken;
      const expiresAt =
        typeof data?.expiresAt === 'number'
          ? data.expiresAt
          : typeof data?.exp === 'number'
            ? data.exp
            : typeof accessToken === 'string'
              ? decodeJwtExp(accessToken)
              : undefined;
      if (
        typeof accessToken !== 'string' ||
        !accessToken ||
        (typeof refreshToken !== 'string' &&
          refreshToken !== null &&
          refreshToken !== undefined) ||
        typeof expiresAt !== 'number'
      ) {
        // response.ok was true here - a real response came back, it just
        // doesn't match the expected shape - so this is a validation
        // failure, not an API/status error.
        fireGtmEvent({
          event: 'errors',
          errorDetails: apiErrorMeta.ERROR_TRACKING_EVENTS.ERROR_TRACKING,
          ...errorTrackingEvent({
            errorType: 'validation',
            errorDescription: AUTH_INVALID_RESPONSE_DESCRIPTION,
            errorMessage: AUTH_INVALID_RESPONSE_MESSAGE,
            screenName: getApiPlatform(),
            service: apiErrorMeta.SERVICE_NAMES.FLIGHTS_AUTHENTICATION,
          }),
        });
        throw new Error(AUTH_INVALID_RESPONSE_MESSAGE);
      }

      return {
        accessToken,
        refreshToken: refreshToken ?? null,
        expiresAt,
      };
    } catch (err) {
      // Covers network failures and anything that threw before or after the
      // response.ok backoff above (e.g. an invalid response body) — those
      // must back off too, or a reconnect just lets every caller retry at
      // once. Skip re-arming if we're already backed off so a burst of calls
      // during the cooldown doesn't keep pushing it out.
      const now = Date.now();
      if (entry.backoffUntil < now) {
        entry.backoffUntil = now + DEFAULT_LOGIN_BACKOFF_MS;
      }
      console.error('❌ [AuthApi] TOKEN ERROR:', err);
      throw err;
    }
  }
}
